
import React, { useEffect, useState } from 'react';
import { Home, Car, ArrowRight, Clock, ShieldCheck, ThumbsUp, Users, Check, FileText, Mail, MessageCircle } from 'lucide-react';
import { Category, PublicStats } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../lib/db';
import { useNavigate } from 'react-router-dom';
import Logo from './ui/Logo';
import SEO from './SEO';

interface HeroSelectionProps {
  theme: 'light' | 'dark';
}

const CountUp = ({ end, duration = 2000, suffix = '', decimals = 0 }: { end: number; duration?: number; suffix?: string; decimals?: number }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuad = 1 - (1 - progress) * (1 - progress);
      setCount(easeOutQuad * end);

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [end, duration]);

  return <>{count.toFixed(decimals)}{suffix}</>;
};

const HeroSelection = ({ theme }: HeroSelectionProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState<PublicStats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
        try {
            const data = await api.public.getStats();
            setStats(data);
        } catch (error) {
            setStats({
                happyCustomers: 0,
                approvedProviders: 0,
                totalLeads: 0,
                avgRating: 4.9
            });
        }
    };
    fetchStats();
  }, []);

  const openCookieSettings = (e: React.MouseEvent) => {
      e.preventDefault();
      window.dispatchEvent(new Event('open_cookie_settings'));
  };

  const handleSelectCategory = (category: Category) => {
      const slug = category === Category.REAL_ESTATE ? 'emlak' : 'vasita';
      navigate(`/talep-olustur/${slug}`);
  };

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      <SEO 
        title="Sen İste, Teklif Cebine Gelsin"
        description="Emlak ve araç alım satım taleplerinizi kolayca oluşturun, kurumsal firmalardan en iyi teklifler cebinize gelsin. Tersine pazar yeri."
        keywords="emlak, satılık daire, kiralık ev, araba, ikinci el araç, oto galeri, emlak ofisi, teklif al, cebime gelsin"
      />
      
      {/* --- HERO SECTION (MAIN) --- */}
      <div className="relative min-h-[calc(100vh-5rem)] flex flex-col items-center justify-center p-4 sm:p-6 overflow-hidden bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-900">
        
        {/* Background Decor */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-teal-400/10 dark:bg-teal-600/10 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-400/10 dark:bg-blue-600/10 rounded-full blur-[100px]"></div>
        </div>

        <div className="text-center max-w-4xl mx-auto mb-8 sm:mb-16 relative z-10 pt-4 sm:pt-4">
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-black text-gray-900 dark:text-white mb-4 sm:mb-6 tracking-tight leading-tight animate-in fade-in slide-in-from-bottom-4 duration-700">
            {t('hero_slogan')}
          </h1>

          <p className="text-xl sm:text-3xl font-bold text-gray-700 dark:text-gray-200 mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
            {t('hero_title_prefix')} <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-blue-600 dark:from-teal-400 dark:to-blue-400">{t('hero_title_brand')}</span>
            <span className="text-gray-700 dark:text-gray-200">{t('hero_title_suffix')}</span>
          </p>

          <p className="text-sm sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed px-2 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            {t('hero_desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 md:gap-12 max-w-5xl w-full relative z-10 pb-8 animate-in fade-in zoom-in-95 duration-700 delay-300">
          {/* Real Estate Card */}
          <div 
            onClick={() => handleSelectCategory(Category.REAL_ESTATE)}
            className="group relative h-[300px] sm:h-[500px] rounded-[2rem] shadow-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-2 hover:shadow-teal-500/20 bg-white dark:bg-gray-800"
          >
            <div className="absolute inset-0 bg-gray-900/40 group-hover:bg-gray-900/30 transition-colors z-10"></div>
            <img 
              src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=1600&auto=format&fit=crop" 
              alt="Emlak" 
              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000"
            />
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-end p-6 sm:p-8 pb-8 sm:pb-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-3 sm:p-4 rounded-full mb-2 sm:mb-4 group-hover:bg-teal-600 group-hover:border-teal-600 transition-all duration-300 shadow-lg">
                <Home className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">{t('card_real_estate')}</h2>
              <p className="text-gray-200 text-center mb-4 sm:mb-6 font-medium text-xs sm:text-sm">{t('card_real_estate_desc')}</p>
              
              <button className="bg-white text-teal-900 px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-bold flex items-center space-x-2 group-hover:bg-teal-600 group-hover:text-white transition-all duration-300 shadow-lg text-sm sm:text-base">
                <span>{t('btn_create_request')}</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Car Card */}
          <div 
            onClick={() => handleSelectCategory(Category.CAR)}
            className="group relative h-[300px] sm:h-[500px] rounded-[2rem] shadow-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-2 hover:shadow-blue-500/20 bg-white dark:bg-gray-800"
          >
            <div className="absolute inset-0 bg-gray-900/40 group-hover:bg-gray-900/30 transition-colors z-10"></div>
            <img 
              src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80" 
              alt="Araba" 
              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000"
            />
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-end p-6 sm:p-8 pb-8 sm:pb-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
               <div className="bg-white/10 backdrop-blur-md border border-white/20 p-3 sm:p-4 rounded-full mb-2 sm:mb-4 group-hover:bg-blue-600 group-hover:border-blue-600 transition-all duration-300 shadow-lg">
                <Car className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">{t('card_car')}</h2>
              <p className="text-gray-200 text-center mb-4 sm:mb-6 font-medium text-xs sm:text-sm">{t('card_car_desc')}</p>
              
              <button className="bg-white text-blue-900 px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-bold flex items-center space-x-2 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-lg text-sm sm:text-base">
                <span>{t('btn_create_request')}</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- VALUE PROPOSITION SECTION --- */}
      <div className="py-12 sm:py-20 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
         <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12 sm:mb-16">
               <h2 className="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white mb-4">{t('why_title')}</h2>
               <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">{t('why_desc')}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
               {[
                  { icon: <ShieldCheck className="w-8 h-8 text-teal-600"/>, title: t('prop_trust_title'), desc: t('prop_trust_desc') },
                  { icon: <Clock className="w-8 h-8 text-blue-500"/>, title: t('prop_time_title'), desc: t('prop_time_desc') },
                  { icon: <ThumbsUp className="w-8 h-8 text-amber-500"/>, title: t('prop_price_title'), desc: t('prop_price_desc') },
                  { icon: <MessageCircle className="w-8 h-8 text-purple-500"/>, title: t('prop_comm_title'), desc: t('prop_comm_desc') }
               ].map((item, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800 p-8 rounded-3xl text-center hover:shadow-lg transition-all duration-300 border border-transparent hover:border-teal-100 dark:hover:border-gray-700">
                     <div className="w-16 h-16 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                        {item.icon}
                     </div>
                     <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                     <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
               ))}
            </div>
         </div>
      </div>

      {/* --- HOW IT WORKS SECTION --- */}
      <div className="py-16 sm:py-24 bg-teal-50/50 dark:bg-gray-800/30 relative overflow-hidden">
         {/* Decorative Background Elements */}
         <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-50">
            <div className="absolute top-20 left-10 w-64 h-64 bg-teal-200 dark:bg-teal-900/20 rounded-full blur-[80px]"></div>
            <div className="absolute bottom-20 right-10 w-64 h-64 bg-blue-200 dark:bg-blue-900/20 rounded-full blur-[80px]"></div>
         </div>

         <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16 sm:mb-20">
               <span className="text-teal-600 dark:text-teal-400 font-bold tracking-widest text-xs uppercase mb-2 block">{t('how_badge')}</span>
               <h2 className="text-3xl sm:text-5xl font-black text-gray-900 dark:text-white mb-6">{t('how_title')}</h2>
               <p className="text-gray-500 dark:text-gray-400 text-base sm:text-lg">{t('how_desc')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
               {/* Connecting Line (Desktop) */}
               <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 border-t-2 border-dashed border-teal-200 dark:border-gray-600 z-0"></div>

               {[
                  { 
                    step: '1', 
                    title: t('step_1_title'), 
                    desc: t('step_1_desc'),
                    icon: <FileText className="w-8 h-8 text-white" />,
                    color: 'from-teal-500 to-blue-500' 
                  },
                  { 
                    step: '2', 
                    title: t('step_2_title'), 
                    desc: t('step_2_desc'),
                    icon: <Mail className="w-8 h-8 text-white" />,
                    color: 'from-blue-500 to-indigo-500' 
                  },
                  { 
                    step: '3', 
                    title: t('step_3_title'), 
                    desc: t('step_3_desc'),
                    icon: <Check className="w-8 h-8 text-white" />,
                    color: 'from-indigo-500 to-purple-500' 
                  }
               ].map((item, idx) => (
                  <div key={idx} className="relative group">
                     {/* Card */}
                     <div className="bg-white dark:bg-gray-800 rounded-[2rem] p-8 shadow-xl border border-gray-100 dark:border-gray-700 relative z-10 transition-all duration-300 group-hover:-translate-y-2 group-hover:shadow-2xl group-hover:border-teal-100 dark:group-hover:border-gray-600">
                        
                        {/* Floating Step Badge */}
                        <div className={`absolute -top-6 left-1/2 transform -translate-x-1/2 w-14 h-14 bg-gradient-to-br ${item.color} rounded-2xl rotate-3 flex items-center justify-center shadow-lg group-hover:rotate-0 transition-all duration-300`}>
                           {item.icon}
                        </div>

                        <div className="mt-10 text-center">
                           <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                           <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                     </div>
                     
                     {/* Step Number Watermark - Fixed opacity to prevent '2' from showing on load if CSS delays */}
                     <div className="absolute -bottom-10 -right-4 text-9xl font-black text-gray-100 dark:text-gray-800/50 select-none z-0 transition-transform duration-300 group-hover:scale-110 opacity-30">
                        {item.step}
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>

      {/* --- STATS SECTION (REAL TIME) --- */}
      <div className="py-16 bg-gray-50 dark:bg-gray-800/50 border-y border-gray-200 dark:border-gray-700">
         <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-gray-200 dark:divide-gray-700">
               {[
                  { val: stats?.happyCustomers || 0, label: t('stat_happy_customers'), suffix: '+' },
                  { val: stats?.approvedProviders || 0, label: t('stat_approved_providers'), suffix: '+' },
                  { val: stats?.totalLeads || 0, label: t('stat_total_leads'), suffix: '' },
                  { val: stats?.avgRating || 0, label: t('stat_score'), decimals: 1, suffix: '' }
               ].map((stat, idx) => (
                  <div key={idx} className="p-4">
                     <div className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-teal-600 to-blue-600 dark:from-teal-400 dark:to-blue-400 mb-2">
                        {stats ? <CountUp end={stat.val} suffix={stat.suffix} decimals={stat.decimals} /> : '-'}
                     </div>
                     <div className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide">{stat.label}</div>
                  </div>
               ))}
            </div>
         </div>
      </div>

      {/* --- FOOTER --- */}
      <footer className="bg-gray-900 text-white py-12 border-t border-gray-800">
         <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-1">
               <div className="flex items-center mb-4">
                  <Logo className="h-12" />
               </div>
               <p className="text-sm text-gray-400 leading-relaxed pr-4 mb-4">{t('footer_intro')}</p>
               {/* Etbis Logo */}
               <div className="mt-4">
                 <a 
                   href="https://etbis.ticaret.gov.tr/tr/Anasayfa/SiteAraSonuc?siteId=08330d44-0c6f-4191-9448-9242a490babb"
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="inline-block hover:opacity-80 transition-opacity"
                   title="ETBİS'e Kayıtlıdır - e-ticaret Bilgi Platformu - Site doğrulama sayfası"
                 >
                   <img 
                     src="/etbis_logo.png" 
                     alt="ETBİS'e Kayıtlıdır - e-ticaret Bilgi Platformu" 
                     className="h-16 sm:h-20 md:h-24 w-auto object-contain"
                   />
                 </a>
               </div>
            </div>
            
            <div className="col-span-1">
               <h3 className="font-bold text-sm mb-4 tracking-wider uppercase text-gray-400">{t('footer_cat')}</h3>
               <ul className="space-y-3 text-sm">
                  <li><a href="#" className="text-gray-300 hover:text-white transition-colors">{t('footer_cat_1')}</a></li>
                  <li><a href="#" className="text-gray-300 hover:text-white transition-colors">{t('footer_cat_2')}</a></li>
                  <li><a href="#" className="text-gray-300 hover:text-white transition-colors">{t('footer_cat_3')}</a></li>
                  <li><a href="#" className="text-gray-300 hover:text-white transition-colors">{t('footer_cat_4')}</a></li>
               </ul>
            </div>

            <div className="col-span-1">
               <h3 className="font-bold text-sm mb-4 tracking-wider uppercase text-gray-400">{t('footer_corp')}</h3>
               <ul className="space-y-3 text-sm">
                  <li><a href="/hakkimizda" className="text-gray-300 hover:text-white transition-colors">{t('footer_corp_1')}</a></li>
                  <li><a href="/nasil-calisir" className="text-gray-300 hover:text-white transition-colors">{t('footer_corp_2')}</a></li>
                  <li><a href="/iletisim" className="text-gray-300 hover:text-white transition-colors">{t('footer_corp_4')}</a></li>
               </ul>
            </div>
            
            <div className="col-span-1">
               <h3 className="font-bold text-sm mb-4 tracking-wider uppercase text-gray-400">Güvenli Ödeme Altyapısı</h3>
               <div className="inline-block">
                 <img 
                    src="/paytr_logo.png" 
                    alt="PayTR Ödeme Altyapısı" 
                    className="h-8 w-auto"
                 />
               </div>
            </div>
         </div>
         <div className="mt-12 pt-8 border-t border-gray-800">
           <div className="flex flex-col md:flex-row justify-between items-center gap-4">
             {/* Center - Links */}
             <div className="flex justify-center gap-6 text-xs text-gray-500">
               <a href="/gizlilik" className="hover:text-white">Gizlilik Politikası</a>
               <a href="/sozlesme" className="hover:text-white">Kullanıcı Sözleşmesi</a>
               <a href="#" onClick={openCookieSettings} className="hover:text-white">Çerez Ayarları</a>
             </div>

             {/* Right side - Copyright */}
             <div className="text-xs text-gray-500 text-center md:text-right">
               &copy; {new Date().getFullYear()} Cebime Gelsin Teknoloji A.Ş. {t('footer_rights')}
             </div>
           </div>
         </div>
      </footer>
    </div>
  );
};

export default HeroSelection;
